import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  fetchConversations,
  fetchMessages,
  deleteConversation as apiDeleteConversation,
  archiveConversation as apiArchiveConversation,
  type ChatConversationSummary,
  type ChatHistoryMessage,
} from './chat.api';

export function useChatHistory() {
  const { session } = useAuth();
  const token = session?.access_token ?? null;
  const isLoggedIn = Boolean(token);

  const [conversations, setConversations] = useState<ChatConversationSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [historyMessages, setHistoryMessages] = useState<ChatHistoryMessage[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const fetchedRef = useRef(false);

  const loadConversations = useCallback(async () => {
    if (!token) return;
    setLoadingList(true);
    try {
      const list = await fetchConversations(token);
      setConversations(list);
    } catch {
      // silent
    } finally {
      setLoadingList(false);
    }
  }, [token]);

  useEffect(() => {
    if (!isLoggedIn) {
      setConversations([]);
      setSelectedId(null);
      setHistoryMessages([]);
      fetchedRef.current = false;
      return;
    }
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      loadConversations();
    }
  }, [isLoggedIn, loadConversations]);

  const selectConversation = useCallback(
    async (conversationId: string | null) => {
      setSelectedId(conversationId);
      setHistoryMessages([]);
      if (!conversationId || !token) return;

      setLoadingMessages(true);
      try {
        const msgs = await fetchMessages(token, conversationId);
        setHistoryMessages(msgs);
      } catch {
        // silent
      } finally {
        setLoadingMessages(false);
      }
    },
    [token],
  );

  const deleteConversation = useCallback(
    async (conversationId: string) => {
      if (!token) return;
      try {
        await apiDeleteConversation(token, conversationId);
        setConversations((prev) => prev.filter((c) => c.id !== conversationId));
        if (selectedId === conversationId) {
          setSelectedId(null);
          setHistoryMessages([]);
        }
      } catch {
        // silent
      }
    },
    [token, selectedId],
  );

  const archiveConversation = useCallback(
    async (conversationId: string) => {
      if (!token) return;
      try {
        await apiArchiveConversation(token, conversationId);
        setConversations((prev) =>
          prev.map((conversation) =>
            conversation.id === conversationId
              ? { ...conversation, status: 'ARCHIVED' }
              : conversation,
          ),
        );
      } catch {
        // silent
      }
    },
    [token],
  );

  const startNewChat = useCallback(() => {
    setSelectedId(null);
    setHistoryMessages([]);
  }, []);

  const refreshList = useCallback(() => {
    loadConversations();
  }, [loadConversations]);

  return {
    isLoggedIn,
    conversations,
    selectedId,
    historyMessages,
    loadingList,
    loadingMessages,
    selectConversation,
    deleteConversation,
    archiveConversation,
    startNewChat,
    refreshList,
  };
}
